import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Home, KeyRound, Lock, Mail, RotateCcw, UserRound } from 'lucide-react';
import logo from '../assets/westcounty-mark.png';
import cityBg from '../assets/city-bg.png';
import LanguagePicker from '../components/LanguagePicker.jsx';
import { authText } from '../config/auth.js';
import { API_BASE_URL } from '../config/api.js';

function Field({ icon, label, required = true, children }) {
  return (
    <label className="auth-field">
      <span>
        {label} {required && <b>*</b>}
      </span>
      <div className="auth-input">
        {icon}
        {children}
      </div>
    </label>
  );
}

const initialForm = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  pin: '',
};

export default function AuthPage({ lang, setLang, mode = 'login' }) {
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [pinStep, setPinStep] = useState(false);
  const [pinIdentity, setPinIdentity] = useState('');
  const [pinEmailMask, setPinEmailMask] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const t = authText[lang];
  const page = mode === 'register' ? t.register : pinStep ? t.pin : t.login;
  const isRegister = mode === 'register';

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendTimer((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendTimer]);

  function checkCaps(event) {
    setCapsLock(Boolean(event.getModifierState && event.getModifierState('CapsLock')));
  }

  function updateForm(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === 'pin' ? value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 7) : value,
    }));
  }

  function saveAuthSession(account) {
    if (!account) return;

    const payload = {
      reg_id: account.reg_id || 0,
      username: account.username || form.username.trim(),
      email: account.email || '',
      verified: account.verified ?? true,
      logged_in_at: Date.now(),
    };

    localStorage.setItem('westcounty-auth', JSON.stringify(payload));
  }

  function continueAfterLogin(message, account = null) {
    if (account) {
      saveAuthSession(account);
    }

    setStatus({ type: 'success', text: message });
    window.setTimeout(() => {
      window.location.href = '/dashboard';
    }, 900);
  }

  async function verifyPin(event) {
    event.preventDefault();
    setStatus({ type: '', text: '' });

    const normalizedPin = /^\d{4}$/.test(form.pin) ? `WC-${form.pin}` : form.pin.toUpperCase();

    if (!/^WC-\d{4}$/.test(normalizedPin)) {
      setStatus({ type: 'error', text: t.meta.pinInvalid });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/verify-pin.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: pinIdentity || form.username.trim(),
          pin: normalizedPin,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setStatus({ type: 'error', text: data.message || t.meta.networkError });
        return;
      }

      continueAfterLogin(data.message || t.meta.pinSuccess, data.account || { username: pinIdentity || form.username.trim() });
    } catch (error) {
      setStatus({ type: 'error', text: t.meta.networkError });
    } finally {
      setSubmitting(false);
    }
  }

  async function resendPin() {
    if (resendTimer > 0 || submitting) return;

    setStatus({ type: '', text: '' });
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/resend-pin.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: pinIdentity || form.username.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setStatus({ type: 'error', text: data.message || t.meta.networkError });
        return;
      }

      setResendTimer(data.cooldown_seconds || 60);
      setStatus({ type: 'success', text: data.message || t.meta.pinResent });
    } catch (error) {
      setStatus({ type: 'error', text: t.meta.networkError });
    } finally {
      setSubmitting(false);
    }
  }

  async function submitForm(event) {
    event.preventDefault();
    setStatus({ type: '', text: '' });

    if (pinStep) {
      await verifyPin(event);
      return;
    }

    if (!isRegister) {
      setSubmitting(true);

      try {
        const response = await fetch(`${API_BASE_URL}/login.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username.trim(),
            password: form.password,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (data.requires_pin) {
          setPinStep(true);
          setPinIdentity(data.username || form.username.trim());
          setPinEmailMask(data.email_mask || 'email kamu');
          setResendTimer(data.cooldown_seconds || 60);
          setStatus({ type: 'info', text: data.message || t.meta.pinRequired });
          return;
        }

        if (!response.ok || !data.success) {
          setStatus({ type: 'error', text: data.message || t.meta.networkError });
          return;
        }

        continueAfterLogin(data.message || t.meta.loginSuccess, data.account || null);
      } catch (error) {
        setStatus({ type: 'error', text: t.meta.networkError });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (form.password !== form.confirmPassword) {
      setStatus({ type: 'error', text: t.meta.passwordMismatch });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/register.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          password: form.password,
          email: form.email.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setStatus({ type: 'error', text: data.message || t.meta.networkError });
        return;
      }

      setForm(initialForm);
      setStatus({ type: 'success', text: data.message || t.meta.registerSuccess });
    } catch (error) {
      setStatus({ type: 'error', text: t.meta.networkError });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="auth-bg" style={{ backgroundImage: `url(${cityBg})` }} />
      <div className="auth-glow auth-glow-one" />
      <div className="auth-glow auth-glow-two" />

      <a className="auth-back auth-home-only" href="/" aria-label={t.meta.backHome} title={t.meta.backHome}>
        <Home size={20} strokeWidth={2.2} />
      </a>

      <div className="auth-language-top">
        <LanguagePicker lang={lang} setLang={setLang} label={t.meta.language} />
      </div>

      <section className="auth-panel" aria-labelledby="auth-title">
        <a className="auth-logo" href="/" aria-label="GTA:WestCounty Home">
          <img src={logo} alt="GTA:WestCounty" />
        </a>

        <header className="auth-heading">
          <h1 id="auth-title">{page.title}</h1>
          <p>{pinStep && pinEmailMask ? `${page.subtitle} ${pinEmailMask}.` : page.subtitle}</p>
        </header>

        <form className="auth-form" onSubmit={submitForm}>
          {pinStep ? (
            <>
              <div className="pin-box">
                <span>{t.meta.pinNotice}</span>
                <strong>{pinEmailMask}</strong>
              </div>
              <Field label={t.fields.pin} icon={<KeyRound size={19} />}>
                <input
                  type="text"
                  inputMode="text"
                  name="pin"
                  value={form.pin}
                  onChange={updateForm}
                  placeholder={t.fields.pinPlaceholder}
                  autoComplete="one-time-code"
                  maxLength="7"
                  required
                />
              </Field>
              <div className="pin-actions">
                <span>{resendTimer > 0 ? t.meta.resendWait.replace('{seconds}', resendTimer) : t.meta.resendReady}</span>
                <button type="button" onClick={resendPin} disabled={resendTimer > 0 || submitting}>
                  <RotateCcw size={15} /> {t.meta.resendPin}
                </button>
              </div>
            </>
          ) : isRegister ? (
            <>
              <Field label={t.fields.username} icon={<UserRound size={19} />}>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={updateForm}
                  placeholder={t.fields.usernamePlaceholder}
                  autoComplete="off"
                  minLength="3"
                  maxLength="24"
                  required
                />
              </Field>

              <Field label={t.fields.email} icon={<Mail size={19} />}>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={updateForm}
                  placeholder={t.fields.emailPlaceholder}
                  autoComplete="off"
                  required
                />
              </Field>
            </>
          ) : (
            <Field label={t.fields.usernameEmail} icon={<UserRound size={19} />}>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={updateForm}
                placeholder={t.fields.usernameEmailPlaceholder}
                autoComplete="off"
                required
              />
            </Field>
          )}

          {!pinStep && (
            <Field label={t.fields.password} icon={<Lock size={19} />}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={updateForm}
                placeholder={t.fields.passwordPlaceholder}
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                minLength="6"
                onKeyUp={checkCaps}
                onBlur={() => setCapsLock(false)}
                required
              />
              <button className="auth-eye" type="button" onClick={() => setShowPassword((value) => !value)} aria-label="Toggle password visibility">
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </Field>
          )}

          {isRegister && !pinStep && (
            <>
              <Field label={t.fields.confirmPassword} icon={<Lock size={19} />}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={updateForm}
                  placeholder={t.fields.confirmPasswordPlaceholder}
                  autoComplete="new-password"
                  minLength="6"
                  onKeyUp={checkCaps}
                  onBlur={() => setCapsLock(false)}
                  required
                />
              </Field>
              <p className="auth-hint">{t.meta.passwordHint}</p>
              <p className="auth-terms">{t.meta.terms}</p>
            </>
          )}


          {!isRegister && !pinStep && (
            <div className="auth-options">
              <label className="auth-remember">
                <input type="checkbox" />
                <span />
                {t.meta.remember}
              </label>
              <a href="/account/forgot-password">{t.meta.forgot}</a>
            </div>
          )}

          {status.text && <div className={`auth-message ${status.type}`}>{status.text}</div>}

          <button className="auth-submit" type="submit" disabled={submitting}>
            {submitting ? t.meta.processing : page.submit}
          </button>
        </form>

        {page.altAction && (
          <>
            <div className="auth-divider"><span>{t.meta.divider}</span></div>

            <p className="auth-switch">
              {page.alt}&nbsp;
              <a href={page.altHref}>{page.altAction}</a>
            </p>
          </>
        )}
      </section>

      <footer className="auth-footer">
        <span>{t.meta.copyright}</span>
      </footer>
    </main>
  );
}
